import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Auth from "../../Auth";
import { sendAppEvent } from "../../Utils";
import { PartnerCodeMetadata } from "~shared/types/partner-code-types";

export interface PartnerCode {
  id: string;
  code: string;
  type: "partner" | "event";
  is_active: boolean;
  credit_amount: number;
  metadata: PartnerCodeMetadata;
  click_count: number;
  signup_count: number;
  conversion_count: number;
  credits_granted: number;
  max_signup_count: number;
  created_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  stats?: {
    clicks: number;
    signups: number;
    conversions: number;
    conversionRate: number;
    signupRate: number;
    totalCreditsGranted: number;
    maxSignups: number;
    remainingSignups: number | null;
  };
}

export interface CreatePartnerCodeParams {
  code: string;
  type?: "partner" | "event";
  credit_amount: number;
  is_active?: boolean;
  metadata?: PartnerCodeMetadata;
  notes?: string;
  max_signup_count?: number;
}

export interface UpdatePartnerCodeParams {
  id: string;
  is_active?: boolean;
  credit_amount?: number;
  metadata?: PartnerCodeMetadata;
  notes?: string;
  type?: "partner" | "event";
  max_signup_count?: number;
}

export interface PartnerCodesFilter {
  type?: "partner" | "event";
  is_active?: boolean;
}

export interface AggregateStats {
  totalCodes: number;
  totalClicks: number;
  totalSignups: number;
  totalConversions: number;
  totalCreditsGranted: number;
}

export const usePartnerCodes = (filters?: PartnerCodesFilter) =>
  useQuery({
    queryKey: ["partnerCodes", filters?.type, filters?.is_active],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append("type", filters.type);
      if (filters?.is_active !== undefined) params.append("is_active", String(filters.is_active));

      const response = await Auth.fetchWithIdToken(`/api/admin/partners?${params.toString()}`);

      if (!response?.success) throw new Error("Failed to fetch partner codes");

      return response.partners as PartnerCode[];
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchOnWindowFocus: true,
  });

export const usePartnerCode = (id: string) =>
  useQuery({
    queryKey: ["partnerCode", id],
    queryFn: async () => {
      const response = await Auth.fetchWithIdToken(`/api/admin/partners/${id}`);

      if (!response?.success) throw new Error("Failed to fetch partner code");

      return response.partner as PartnerCode;
    },
    enabled: !!id,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

export const usePartnerCodeByCode = (code: string) =>
  useQuery({
    queryKey: ["partnerCodeByCode", code],
    queryFn: async () => {
      const response = await Auth.fetchWithIdToken(`/api/admin/partners/by-code/${code}`);

      if (!response?.success) throw new Error("Failed to fetch partner code");

      return response.partner as PartnerCode;
    },
    enabled: !!code,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

export const useCreatePartnerCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreatePartnerCodeParams) => {
      const response = await Auth.fetchWithIdToken("/api/admin/partners", {
        method: "PUT",
        ...params,
      });

      if (!response?.success) {
        throw new Error(response?.error || "Failed to create partner code");
      }

      return response.partner as PartnerCode;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partnerCodes"] });
      queryClient.invalidateQueries({ queryKey: ["partnerStats"] });
      sendAppEvent("Admin Partner Code Created");
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
};

export const useUpdatePartnerCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdatePartnerCodeParams) => {
      const response = await Auth.fetchWithIdToken(`/api/admin/partners/${id}`, {
        method: "PUT",
        ...updates,
      });

      if (!response?.success) {
        throw new Error(response?.error || "Failed to update partner code");
      }

      return response.partner as PartnerCode;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["partnerCodes"] });
      queryClient.invalidateQueries({ queryKey: ["partnerCode", data.id] });
      queryClient.invalidateQueries({ queryKey: ["partnerStats"] });
      sendAppEvent("Admin Partner Code Updated");
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
};

export const useDeletePartnerCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await Auth.fetchWithIdToken(`/api/admin/partners/${id}`, {
        method: "DELETE",
      });

      if (!response?.success) {
        throw new Error(response?.error || "Failed to delete partner code");
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partnerCodes"] });
      queryClient.invalidateQueries({ queryKey: ["partnerStats"] });
      sendAppEvent("Admin Partner Code Deleted");
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
  });
};

export const usePartnerStats = (filters?: PartnerCodesFilter) =>
  useQuery({
    queryKey: ["partnerStats", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append("type", filters.type);
      if (filters?.is_active !== undefined) params.append("is_active", String(filters.is_active));

      const response = await Auth.fetchWithIdToken(`/api/admin/partners-stats?${params.toString()}`);

      if (!response?.success) throw new Error("Failed to fetch partner stats");

      return response.stats as AggregateStats;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchInterval: 60000,
  });
